import { useQueryClient } from "@tanstack/react-query";
import { ValidationError } from "joi";
import isEqual from "lodash-es/isEqual";

// Interfaces
import { IInputSalesLogsEmailDistribution, TEmailDistributionUpdateParams } from "@/Settings/types/SystemSettings/emailDistribution";
import { IMappedPosition } from "@/Common/types/positions/api";

// Helpers
import findError from "@/Settings/helpers/SystemSettings/errors/findError";
import getSelectEmails from "@/Settings/helpers/SystemSettings/emailDistribution/getSelectEmails";

// MUI
import { Box, Grid } from "@mui/material";

// Components
import InfoSubTitle from "../../../common/InfoSubTitle";

// Layouts
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Icons
import TrashIconWhite from "@/assets/icons/motivators/spiffSetup/trash-white.svg";

const getSelectedEmployees = (
	employees: { employeeId: string }[] = [],
	selected: {
		employeeId: string;
		_id: string;
	}[] = []
) => {
	const mappedSelected = selected.map((item) => item.employeeId);
	return employees?.filter((employee) => mappedSelected.includes(employee.employeeId));
};

const selectableDailyDist = Array.from({ length: 6 }, (_, i) => i + 1).map((key) => ({ id: key, label: `${key}` }));

type TEmailDistributionLayoutProps = {
	locationId: string;
	label: string;
	index: number;
	distribution: IInputSalesLogsEmailDistribution;
	mappedPositions?: Pick<IMappedPosition, "systemPosition">[];
	errors?: ValidationError;
	disabled: boolean;
	onRemove: (index: number) => void;
	onActionUpdate: ({ index, key, value }: TEmailDistributionUpdateParams) => void;
};

const selectPlaceholder = "Select an Option";

const EmailDistributionLayout = (props: TEmailDistributionLayoutProps) => {
	const { label, index, distribution, onRemove, onActionUpdate, mappedPositions = [], locationId, errors, disabled } = props;
	const queryClient = useQueryClient();

	const { getEmployees: employeeListData } = queryClient.getQueryData(["systemSettingsAdditionalInfo", locationId]) as {
		getEmployees: { employeeId: string }[];
	};

	const selectedToPositions = (distribution?.toPositions || []).map((item) => ({ id: item, label: item }));

	const selectedToIndividuals = getSelectedEmployees(employeeListData, distribution?.toIndividuals);

	const handleFormatChange = (selected: { id: string; label: string }) => {
		onActionUpdate({ index, key: "exportFormat", value: selected.id });
	};

	const handleDailyDistribution = (selected: { id: number; label: string }) => {
		onActionUpdate({ index, key: "dailyDistributions", value: selected.id });
	};

	const handleToPositions = (selected: { label: string; _id: string }[]) => {
		if (!isEqual(selectedToPositions, selected)) {
			onActionUpdate({ index, key: "toPositions", value: selected.map((item) => item.label) });
		}
	};

	const handleToIndividuals = (selected: { employeeId: string; _id: string }[]) => {
		if (!isEqual(selectedToIndividuals, selected)) {
			onActionUpdate({
				index,
				key: "toIndividuals",
				value: selected.map((item) => ({ _id: item._id, employeeId: item.employeeId })),
			});
		}
	};

	const handleToNonEmployees = (emailValues: string) => {
		const splitEmails: string[] = emailValues?.split(",").map((element) => element.trim());
		onActionUpdate({ index, key: "toEmails", value: splitEmails });
	};

	return (
		<Grid container item>
			<Box className="flex items-center !mb-[24px]">
				<InfoSubTitle title={label} className="!mr-[10px]" />
				<DoubleCheckingButton
					title={"Remove Franchise"}
					endIcon={<img src={TrashIconWhite} />}
					mainButtonClasses={"btn-simple-remove btn-pack-remove"}
					confirmationSx={{ right: 0, left: "10px", width: "150px !important" }}
					onDeleteAction={() => onRemove(index)}
				/>
			</Box>
			<Grid container flexDirection="column">
				<Grid container flexDirection="row" justifyContent="flex-start" item sx={{ width: "100%" }}>
					<Grid item xs={3} className="min-w-72 mr-[15px]">
						<InfoSubTitle title="Export Format" className="!mb-[12px]" />
						<SmartSelector
							selectBy={"id"}
							showBy={"label"}
							options={[
								{ id: "csv", label: "csv" },
								{ id: "xlsx", label: "xlsx" },
								{ id: "pdf", label: "pdf" },
							]}
							selected={distribution?.exportFormat}
							inputProps={{
								placeholder: selectPlaceholder,
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							disabled={disabled}
							error={!!findError(`[${index}].exportFormat`, errors)}
							sx={{ width: "200px" }}
							onChangeAction={(value) => handleFormatChange(value as { id: string; label: string })}
						/>
					</Grid>
					<Grid item xs={3} className="min-w-72">
						<InfoSubTitle title="Number of Daily Distributions" className="!mb-[12px]" />
						<SmartSelector
							selectBy={"label"}
							showBy={"label"}
							options={selectableDailyDist || []}
							selected={{ label: distribution?.dailyDistributions ?? 0 }}
							inputProps={{
								placeholder: selectPlaceholder,
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							disabled={disabled}
							error={!!findError(`[${index}].dailyDistributions`, errors)}
							sx={{ width: "200px" }}
							onChangeAction={(value) => handleDailyDistribution(value as { id: number; label: string })}
						/>
					</Grid>
				</Grid>
				<Grid item container className="!mt-[24px]">
					<Grid item xs={3} className="!mr-[12px]">
						<InfoSubTitle title="To Positions:" className="!mb-[12px]" />
						<SmartSelector
							multiple
							tagsVariant={"text"}
							selectBy={"label"}
							showBy={"label"}
							options={mappedPositions.map((item) => ({ id: item.systemPosition, label: item.systemPosition }))}
							selected={selectedToPositions}
							inputProps={{
								placeholder: selectPlaceholder,
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							clearable
							disabled={disabled}
							error={!!findError(`[${index}].toPositions`, errors)}
							sx={{ width: "200px" }}
							onChangeAction={(values) => handleToPositions(values as { label: string; _id: string }[])}
						/>
					</Grid>
					<Grid item xs={3}>
						<InfoSubTitle title="To Individuals:" className="!mb-[12px]" />
						<SmartSelector
							multiple
							tagsVariant={"text"}
							selectBy={"employeeId"}
							showBy={"fullName"}
							options={employeeListData || []}
							selected={selectedToIndividuals}
							inputProps={{
								placeholder: selectPlaceholder,
							}}
							listBoxProps={{
								sx: { maxHeight: "220px" },
							}}
							clearable
							disabled={disabled}
							error={!!findError(`[${index}].toIndividuals`, errors)}
							sx={{ width: "200px" }}
							onChangeAction={(values) => handleToIndividuals(values as { employeeId: string; _id: string }[])}
						/>
					</Grid>
					<Grid item xs={3}>
						<InfoSubTitle title="To Non-Employees:" className="!mb-[12px]" />
						<SimpleTextField
							sx={{ width: "200px" }}
							editable
							outlined
							onActionUpdate={handleToNonEmployees}
							text={getSelectEmails(distribution?.toEmails) || ""}
							placeholder={"jhon@gmail.com, d.smith@hotmail.com"}
							error={!!findError(`[${index}].toEmails`, errors)}
							disabled={disabled}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default EmailDistributionLayout;
